/**
 * Shared workspace display helpers.
 *
 * Sprint 10.5 introduced "domain-first naming" for workspace cards: the
 * website domain is rendered as the primary title with the business name as a
 * smaller subtitle. These helpers centralize that logic so every surface that
 * renders a workspace (list, dashboard, detail, sidebar) is consistent.
 */

/**
 * Extract a bare hostname from a website URL. Strips protocol, `www.` prefix,
 * path, query, and trailing slashes. Returns an empty string for unusable
 * input so callers can fall back to the workspace name.
 */
export function extractDomain(url: string | null | undefined): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withScheme);
    const host = parsed.hostname.toLowerCase();
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    return trimmed
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0]
      .split('?')[0]
      .toLowerCase();
  }
}

/**
 * Pick a primary (domain) and secondary (name) label for a workspace.
 * Falls back to the workspace name as primary when there is no website_url.
 */
export function workspaceTitles(ws: {
  name: string;
  website_url?: string | null;
}): { primary: string; secondary: string | null } {
  const domain = extractDomain(ws.website_url);
  if (domain) {
    return { primary: domain, secondary: ws.name };
  }
  return { primary: ws.name, secondary: null };
}
